import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { useChatGraphBuildPolling } from "@/features/chat/hooks/useChatGraphBuildPolling";
import { useChatSendMessage, useChatThreadMessages } from "@/features/chat/hooks/useChatMessages";
import {
	useChatCreateThread,
	useChatDeleteThread,
	useChatThreadList,
	useChatThreadSelection,
} from "@/features/chat/hooks/useChatThreads";

interface UseChatbotPanelParams {
	/** When set (e.g. notebook single-workspace view), thread list is scoped to this workspace. */
	workspaceId?: string | null;
	selectedDocumentIds?: string[];
	/** Currently selected/expanded graph in the workspace; used for send-message context and optional thread filter. */
	selectedGraphId?: string | null;
	onGraphGenerated?: (graphId: string) => void;
}

/**
 * Chat panel: composes thread list, selection, messages, create/delete/send and job polling.
 * With workspaceId, threads are listed for the whole workspace (all graphs in it). selectedGraphId
 * is still sent with messages so QA can use that graph when one is expanded.
 */
export const useChatbotPanel = ({
	workspaceId,
	selectedDocumentIds = [],
	selectedGraphId,
	onGraphGenerated,
}: UseChatbotPanelParams) => {
	const [input, setInput] = useState("");
	const [pendingUserMessage, setPendingUserMessage] = useState<{
		threadId: string | null;
		content: string;
	} | null>(null);
	const scrollRef = useRef<HTMLDivElement>(null);
	const queryClient = useQueryClient();
	const titledThreadIdsRef = useRef<Set<string>>(new Set());

	const { threads } = useChatThreadList(workspaceId);
	const { setPollingJobId, jobStatus } = useChatGraphBuildPolling(onGraphGenerated);
	const { selectedThreadId, setSelectedThreadId, handleSelectThread } = useChatThreadSelection(workspaceId, threads);
	const pendingMessageForSelectedThread =
		pendingUserMessage && pendingUserMessage.threadId === selectedThreadId ? pendingUserMessage.content : null;
	const { displayMessages } = useChatThreadMessages(selectedThreadId, pendingMessageForSelectedThread);

	const createThreadMutation = useChatCreateThread(workspaceId, setSelectedThreadId);
	const deleteThreadMutation = useChatDeleteThread(workspaceId, selectedThreadId, threads, setSelectedThreadId);
	const sendMutation = useChatSendMessage(
		selectedThreadId,
		setSelectedThreadId,
		workspaceId,
		(_msg: string | null) => {
			setPendingUserMessage(null);
		},
		setPollingJobId,
	);

	const scrollToBottom = useCallback(() => {
		requestAnimationFrame(() => {
			if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
		});
	}, []);

	useEffect(() => {
		if (!displayMessages.length && !sendMutation.isPending) return;
		scrollToBottom();
	}, [displayMessages.length, sendMutation.isPending, scrollToBottom]);

	const handleSubmit = useCallback(
		async (e: React.FormEvent) => {
			e.preventDefault();
			const trimmed = input.trim();
			if (!trimmed || sendMutation.isPending || createThreadMutation.isPending) return;

			setInput("");

			// If no thread selected, create one first so the first message creates a thread automatically
			let threadIdToUse = selectedThreadId;
			if (!threadIdToUse) {
				try {
					const newThread = await createThreadMutation.mutateAsync({
						workspaceId: workspaceId ?? undefined,
					});
					threadIdToUse = newThread.id;
				} catch (err) {
					console.error("Failed to create thread", err);
					return;
				}
			}

			setPendingUserMessage({
				threadId: threadIdToUse,
				content: trimmed,
			});

			const payload = {
				message: trimmed,
				...(selectedDocumentIds.length > 0 && { documentIds: [...selectedDocumentIds] }),
				...(selectedGraphId && { graphId: selectedGraphId }),
				...(workspaceId && { workspaceId }),
				threadId: threadIdToUse,
			};

			sendMutation.mutate(payload, {
				onSuccess: async (data) => {
					const finalThreadId = data.threadId ?? threadIdToUse;
					if (!finalThreadId) return;

					// Only set a title once per thread (first successful invoke)
					if (titledThreadIdsRef.current.has(finalThreadId)) return;

					// Generate a simple title from the first user message
					const baseTitle = trimmed.replace(/\s+/g, " ").trim();
					if (!baseTitle) return;
					const title = baseTitle.length > 80 ? `${baseTitle.slice(0, 77)}...` : baseTitle;

					try {
						// Lazy-load chat service to avoid circular deps at module init
						const chatModule = await import("@/features/chat/services/chatService");
						const updatedThread = await chatModule.chatService.updateThread(finalThreadId, { title });

						// Update thread list cache
						const threadListKey = ["chatThreads", workspaceId ?? null] as const;
						queryClient.setQueryData(threadListKey, (old: unknown) => {
							if (!Array.isArray(old)) return old;
							return (old as unknown[]).map((thread) =>
								(thread as { id?: string }).id === updatedThread.id ? updatedThread : thread,
							);
						});

						// Update single thread cache
						queryClient.setQueryData(["chatThread", finalThreadId], (old: unknown) => {
							if (!old || typeof old !== "object") return old;
							return { ...(old as Record<string, unknown>), title: updatedThread.title };
						});

						titledThreadIdsRef.current.add(finalThreadId);
					} catch (error) {
						// Failing to set the title should not break the chat flow
						console.error("Failed to set thread title from first message", error);
					}
				},
			});
		},
		[
			input,
			sendMutation,
			createThreadMutation,
			selectedDocumentIds,
			selectedGraphId,
			selectedThreadId,
			queryClient,
			workspaceId,
		],
	);

	const handleNewThread = useCallback(() => {
		createThreadMutation.mutate({ workspaceId: workspaceId ?? undefined });
	}, [createThreadMutation, workspaceId]);

	const handleDeleteThread = useCallback(
		(threadId: string, e: React.MouseEvent) => {
			e.stopPropagation();
			if (confirm("Are you sure you want to delete this thread?")) {
				deleteThreadMutation.mutate(threadId);
			}
		},
		[deleteThreadMutation],
	);

	return {
		threads,
		selectedThreadId,
		displayMessages,
		jobStatus,
		input,
		setInput,
		handleSubmit,
		handleNewThread,
		handleDeleteThread,
		handleSelectThread,
		sendIsPending: sendMutation.isPending || createThreadMutation.isPending,
		scrollRef,
	};
};
